/* eslint-disable react/static-property-placement */
// @flow

import * as React from 'react';
import classnames from 'classnames';
import ReactHTMLParser from 'react-html-parser';
import {
  Analytics,
  AnalyticsLocationConsumer,
  getTrackingQueryString,
  trackEvent
} from 'latitude-analytics';
// import { Link } from '@latitude/link';
import { Link } from '../Link/Link';
import './_login-form.scss';

export type CardTypeLink = {
  url: string, // url for the link
  target?: string // target value for link
};
export type CardType = {
  id: string, // card type identifier
  name: string, // card name display text
  login?: CardTypeLink, // login url
  upload?: CardTypeLink, // upload doc url
  register?: CardTypeLink // register url
};
export type LoginFormDataType = {
  // credit cards
  showCardsPane?: boolean, // Shows the Cards pane only if this prop is set to true
  creditCardsPaneTitle?: string,
  creditCardsSelectDefaultText?: string,
  creditCardsLoginLinkText?: string,
  creditCardsUploadLinkText?: string,
  creditCardsRegisterLinkText?: string,
  // lpay
  showLpayPane?: boolean, // Shows the LatitudePay pane only if this prop is set to true
  lpayPaneTitle?: string,
  lpayPaneIntro?: string,
  lpayLoginLinkText?: string,
  lpayLoginLinkUrl: string,
  lpaySignupLinkText?: string,
  lpaySignupLinkUrl: string,
  // loans
  showLoansPane?: boolean, // Shows the Loans pane only if this prop is set to true
  loansPaneTitle?: string,
  loansPaneIntro?: string,
  loansLoginLinkText?: string,
  loansLoginLinkUrl: string,
  // insurance
  showInsurancePane?: boolean, // Shows the Insurance pane only if this prop is set to true
  insuranceLoginLinkText?: string,
  insuranceLoginLinkUrl: string
};

type Props = {
  analytics: Analytics,
  isOpen: boolean, // True if the Login form is open
  loginForm: LoginFormDataType, // login form content data
  cardTypes: Array<CardType>, // List of the Credit card types, along with register / Login URLs
  openElement: null | HTMLElement, // HTML element of the Logon button
  onRequestClose: () => void // Call this to request the Login form close
};

type State = {
  selectedCard: ?CardType
};

const CardTypeLinkDivider = () => (
  <div className="static-text" style={{ margin: '0 0.7rem' }}>
    or
  </div>
);

class LoginForm extends React.Component<Props, State> {
  wrapperRef: { current: null | HTMLDivElement };

  static defaultProps = {
    isStickyOpen: false // eslint-disable-line react/default-props-match-prop-types
  };

  constructor(props: Props) {
    super(props);
    this.wrapperRef = React.createRef();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // selected card is null by default
    this.state = { selectedCard: null };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick, false);
    document.addEventListener('keydown', this.handleKeyDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  getSelectedCardName(cardId: string) {
    const selectedCardType = this.props.cardTypes.find(
      (cardType: CardType) => cardType.id === cardId
    );
    return selectedCardType ? selectedCardType.name : null;
  }

  /**
   * Handles selection and tracking of credit card type from drop-down box
   */
  handleCardSelectChange = (
    event: SyntheticEvent<HTMLSelectElement>,
    analytics: Analytics,
    location: string
  ) => {
    const { cardTypes } = this.props;
    const selectedItemTextValue =
      event.target.options[event.target.selectedIndex].text;

    this.setState({
      selectedCard: cardTypes.filter(
        (card: CardType) => card.id === event.currentTarget.value
      )[0]
    });

    trackEvent(analytics, {
      category: 'drop-down',
      action: 'in-page-navigation',
      label: selectedItemTextValue,
      location
    });
  };

  /*
   * Close the popup on click outside of the search form
   * unless the click was on Login Button
   */
  handleClick = (ev: MouseEvent) => {
    if (!this.props.isOpen) return;
    const clickTarget = ev.target;
    const wrapperNode = this.wrapperRef && this.wrapperRef.current;
    const { openElement } = this.props;
    if (
      wrapperNode &&
      clickTarget instanceof Node &&
      !wrapperNode.contains(clickTarget) &&
      (!openElement || !openElement.contains(clickTarget))
    ) {
      this.props.onRequestClose();
    }
  };

  /*
   * Close the popup on Esc key,
   */
  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.props.isOpen) return;
    if (event.keyCode === 27) {
      this.props.onRequestClose();
    }
  };

  render() {
    const { analytics } = this.props;
    const { selectedCard } = this.state;
    const selectedCardName = selectedCard
      ? this.getSelectedCardName(selectedCard.id)
      : null;

    // work out links url
    const selectedCardLoginUrl =
      selectedCard && selectedCard.login && selectedCard.login.url;
    const selectedCardUploadUrl =
      selectedCard && selectedCard.upload && selectedCard.upload.url;
    const selectedCardRegisterUrl =
      selectedCard && selectedCard.register && selectedCard.register.url;

    // work out links hrefs
    const selectedCardLoginHref = selectedCardLoginUrl
      ? `${selectedCardLoginUrl}${getTrackingQueryString(analytics, {
          text: 'Login',
          location: 'login-popup',
          context: selectedCardName
        })}`
      : null;
    const selectedCardUploadHref = selectedCardUploadUrl
      ? `${selectedCardUploadUrl}${getTrackingQueryString(analytics, {
          text: 'Upload',
          location: 'login-popup',
          context: selectedCardName
        })}`
      : null;
    const selectedCardRegisterHref = selectedCardRegisterUrl
      ? `${selectedCardRegisterUrl}${getTrackingQueryString(analytics, {
          text: 'Register',
          location: 'login-popup',
          context: selectedCardName
        })}`
      : null;

    // work out links targets
    const selectedCardLoginTarget =
      (selectedCard && selectedCard.login && selectedCard.login.target) || null;
    const selectedCardUploadTarget =
      (selectedCard && selectedCard.upload && selectedCard.upload.target) ||
      null;
    const selectedCardRegisterTarget =
      (selectedCard && selectedCard.register && selectedCard.register.target) ||
      null;

    // temp fix for link overriding default productId
    const DEFAULT_PRODUCT_ID = ['BPAULFIST', 'PLAULF-WEB', 'MLAULF-WEB']

    return (
      <AnalyticsLocationConsumer>
        {(location: string) => (
          <div
            ref={this.wrapperRef}
            className={classnames(
              'login-form',
              this.props.className,
              this.props.isOpen ? 'login-form--is-open' : ''
            )}
          >
            <div className="row existing-customer">
            {this.props.showCardsPane && (
              <section className="col-12 existing-customer__section">
                <div className="login-form__panel">
                  <h5 className="text--bold">
                    {ReactHTMLParser(
                      this.props.loginForm.creditCardsPaneTitle ||
                        'Credit Cards'
                    )}
                  </h5>
                  <select
                    className="styled-select" /* js-siteHeader__personalised-link__select */
                    id="form-card-login__card"
                    onChange={(event: SyntheticEvent<HTMLSelectElement>) =>
                      this.handleCardSelectChange(event, analytics, location)
                    }
                  >
                    <option value="">
                      {ReactHTMLParser(
                        this.props.loginForm.creditCardsSelectDefaultText ||
                          'Select a card'
                      )}
                    </option>
                    {this.props.cardTypes.map((card: CardType) => (
                      <option key={card.id} value={card.id}>
                        {card.name}
                      </option>
                    ))}
                  </select>

                  {/* Show buttons if a card type is selected */}
                  {this.state.selectedCard && (
                    <div className="personalised-links">
                      {/* login link */}
                      {!!selectedCardLoginHref && (
                        <Link
                          data-trackid="nav--login"
                          className="trackcustomanalytics"
                          href={selectedCardLoginHref}
                          target={selectedCardLoginTarget}
                          trackEventData={{
                            action: 'service-centre-link',
                            location
                          }}
                          trackProductId={DEFAULT_PRODUCT_ID}
                        >
                          {this.props.loginForm.creditCardsLoginLinkText ||
                            'Login'}
                        </Link>
                      )}
                      {/* upload link */}
                      {!!selectedCardUploadHref && (
                        <>
                          <CardTypeLinkDivider />
                          <Link
                            data-trackid="nav--upload"
                            className="trackcustomanalytics"
                            href={selectedCardUploadHref}
                            target={selectedCardUploadTarget}
                            trackEventData={{ location }}
                          >
                            {this.props.loginForm.creditCardsUploadLinkText ||
                              'Upload documents'}
                          </Link>
                        </>
                      )}
                      {/* register link */}
                      {!!selectedCardRegisterHref && (
                        <>
                          <CardTypeLinkDivider />
                          <Link
                            data-trackid="nav--register"
                            className="trackcustomanalytics"
                            href={selectedCardRegisterHref}
                            target={selectedCardRegisterTarget}
                            trackEventData={{
                              action: 'service-centre-link',
                              location
                            }}
                            trackProductId={DEFAULT_PRODUCT_ID}
                          >
                            {this.props.loginForm.creditCardsRegisterLinkText ||
                              'Register'}
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </section>
              )}
              {this.props.showLpayPane && (
              <section className="col-12 existing-customer__section">
                <div className="login-form__panel">
                  <h5 className="text--bold">
                    {ReactHTMLParser(
                      this.props.loginForm.lpayPaneTitle || 'LatitudePay'
                    )}
                  </h5>
                  <p className="static-text">
                    {ReactHTMLParser(
                      this.props.loginForm.lpayPaneIntro ||
                        'Spread the cost of your purchases, with no interest, ever. Enjoy it now.'
                    )}
                  </p>
                  <Link
                    className="opzttag148825656loans"
                    href={this.props.loginForm.lpayLoginLinkUrl || ''}
                    trackEventData={{ location }}
                    trackProductId={['BPAULFIST']}
                  >
                    {this.props.loginForm.lpayLoginLinkText ||
                      'Login to LatitudePay'}
                  </Link>
                  <p className="static-text" style={{ margin: '0 0.7rem' }}>
                    or
                  </p>
                  <Link
                    className="opzttag148825656loans"
                    href={this.props.loginForm.lpaySignupLinkUrl || ''}
                    trackEventData={{ location }}
                    trackProductId={['BPAULFIST']}
                  >
                    {this.props.loginForm.lpaySignupLinkText ||
                      'Sign up to LatitudePay'}
                  </Link>
                </div>
              </section>
              )}
              {this.props.showLoansPane && (
              <section className="col-12 existing-customer__section">
                <div className="login-form__panel">
                  <h5 className="text--bold">
                    {ReactHTMLParser(
                      this.props.loginForm.loansPaneTitle || 'Loans'
                    )}
                  </h5>
                  <p className="static-text">
                    {ReactHTMLParser(
                      this.props.loginForm.loansPaneIntro ||
                        'Login to continue your application, upload documents, or accept contracts.'
                    )}
                  </p>
                  <Link
                    className="opzttag148825656loans"
                    href={this.props.loginForm.loansLoginLinkUrl || ''}
                    trackEventData={{ location }}
                    trackProductId={DEFAULT_PRODUCT_ID}
                  >
                    {this.props.loginForm.loansLoginLinkText ||
                      'Login to Loan Applications'}
                  </Link>
                </div>
              </section>
              )}
              {this.props.showInsurancePane && (
                <section className="col-12 existing-customer__section">
                  <div className="login-form__panel">
                    <h5 className="text--bold">
                      {ReactHTMLParser(
                        this.props.loginForm.loansPaneTitle ||
                          'Insurance - Repayment Protection'
                      )}
                    </h5>
                    <p className="static-text">
                      {ReactHTMLParser(
                        this.props.loginForm.loansPaneIntro ||
                          'Login to update details, find important documents and make a claim.'
                      )}
                    </p>
                    <Link
                      className="opzttag148825656loans"
                      href={this.props.loginForm.insuranceLoginLinkUrl || ''}
                      trackEventData={{ location }}
                      trackProductId={DEFAULT_PRODUCT_ID}
                    >
                      {this.props.loginForm.insuranceLoginLinkText ||
                        'Login to Latitude Insurance Portal'}
                    </Link>
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </AnalyticsLocationConsumer>
    );
  }
}

export default LoginForm;
